<template>
  <div class="stacked-document-display">
    <div v-if="documents.length > 0" class="documents-stack">
      <!-- Loop through all documents and display them -->
      <div
        v-for="(document, index) in documents"
        :key="index"
        class="document-item"
      >
        <div class="document-container">
          <!-- Document Preview -->
          <div class="document-preview">
            <!-- Show image if it's an image and we have preview data -->
            <div class="image-container" v-if="isImageFile(document.type) && (document.preview || document.dataUrl)">
              <img
                :src="document.preview || document.dataUrl"
                :alt="document.name"
                class="document-image"
                @error="handleImageError"
              />

              <!-- Add Live Transcript component -->
              <live-transcript
                v-if="isPlaying"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :visible="isPlaying"
              />
            </div>

            <!-- If it's an image but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isImageFile(document.type)" class="image-placeholder">
              <div class="placeholder-icon">🖼️</div>
              <div class="image-name">{{ document.name }}</div>
              <div class="image-info">Image preview not available</div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-if="isPlaying"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :visible="isPlaying"
              />
            </div>

            <!-- Non-image document (PDF, DOCX, etc) -->
            <div v-else class="file-icon-container">
              <div class="file-icon" :class="getFileIconClass(document.type)">
                {{ getFileExtension(document.name).toUpperCase() }}
              </div>
              <div class="document-name">{{ document.name }}</div>

              <!-- Add Live Transcript component for document files -->
              <live-transcript
                v-if="isPlaying"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :visible="isPlaying"
                :maxLength="100"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No documents placeholder -->
    <div v-else class="no-documents">
      <div class="placeholder-icon">📄</div>
      <p>No documents available</p>

      <!-- Add Live Transcript component for empty state -->
      <live-transcript
        v-if="isPlaying"
        :voice-speed="voiceSpeed"
        :text="activeSegmentContent"
        :speaker="activeSegmentSpeaker"
        :speaker-image="speakerImage"
        :visible="isPlaying"
      />
    </div>
  </div>
</template>

<script>
import LiveTranscript from '../components/LiveTranscript.vue';

export default {
  name: 'StackedDocumentDisplay',
  components: {
    LiveTranscript
  },
  props: {
    voiceSpeed: {
      type: Number,
      default: 1
    },
    speakerImage: {
      type: String,
      default: '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg'
    },
    documents: {
      type: Array,
      default: () => []
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    activeSegmentContent: {
      type: String,
      default: ''
    },
    activeSegmentSpeaker: {
      type: String,
      default: ''
    }
  },
  methods: {
    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    getFileExtension(filename) {
      if (!filename) return '';
      return filename.split('.').pop() || '';
    },

    getFileIconClass(fileType) {
      if (!fileType) return 'generic-icon';
      if (fileType.includes('pdf')) return 'pdf-icon';
      if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
      if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
      if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';
      return 'generic-icon';
    },

    handleImageError(e) {
      console.warn('Image load error:', e.target.alt);
      e.target.style.display = 'none';

      // Create a placeholder
      const placeholder = document.createElement('div');
      placeholder.className = 'file-icon generic-icon';
      placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
      e.target.parentNode.appendChild(placeholder);

      // Add a message about the failed load
      const errorMsg = document.createElement('div');
      errorMsg.className = 'image-error-message';
      errorMsg.textContent = 'Image could not be loaded';
      e.target.parentNode.appendChild(errorMsg);
    }
  }
}
</script>

<style scoped>
.stacked-document-display {
  width: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  margin-bottom: 1rem;
  position: relative;
}

.documents-counter {
  text-align: center;
  padding: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(4, 255, 134, 0.1);
  border-radius: 999px;
  display: inline-block;
}

.documents-stack {
  display: flex;
  flex-direction: column;
}

.document-item {
  position: relative;
}

.document-number {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(4, 255, 134, 0.8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  z-index: 2;
}

.document-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.3);
  position: relative;
  padding: 0rem 1rem 1rem 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.document-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.document-image {
  width: 100%;
  max-height: 600px;
  object-fit: contain;
  border-radius: 4px;
}

.image-placeholder {
  width: 100%;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 1rem;
  position: relative;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  opacity: 0.7;
}

.image-name {
  font-weight: 500;
  text-align: center;
  margin-bottom: 0.5rem;
  max-width: 100%;
  word-break: break-word;
}

.image-info {
  font-size: 0.875rem;
  color: #999;
  text-align: center;
}

.image-error-message {
  color: #ff6b6b;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
}

.file-icon-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  position: relative;
  width: 100%;
}

.file-icon {
  width: 120px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 2rem;
  color: white;
  position: relative;
}

.file-icon:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #e0e0e0;
  clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
}

.file-icon:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #c0c0c0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
}

.pdf-icon:before { background: #FF5722; }
.doc-icon:before { background: #2196F3; }
.xls-icon:before { background: #4CAF50; }
.ppt-icon:before { background: #FF9800; }
.generic-icon:before { background: #9E9E9E; }

.document-name {
  font-size: 1rem;
  text-align: center;
  max-width: 300px;
  word-break: break-word;
  margin-top: 1rem;
}

.no-documents {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #999;
  position: relative;
  min-height: 200px;
}

@media (max-width: 768px) {
  .document-container {
    min-height: 150px;
  }

  .document-image {
    max-height: 250px;
  }

  .file-icon {
    width: 80px;
    height: 100px;
    font-size: 1.5rem;
  }
}
</style>
